import {
  createAction,
  createSlice,
  ActionReducerMapBuilder
} from '@reduxjs/toolkit';
import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';

type AadToken = {
  accessToken: string;
};
type AadAccount = {
  '@odata.context': string;
  employeeId: string;
  displayName: string;
  userPrincipalName: string;
};

export type AadData = {
  token?: AadToken;
  account?: AadAccount;
};

export type AadState = {
  isLoading: boolean;
  errors: null | NormalizedError[];
  data: AadData;
};

export const loginSuccess = createAction<AadData>('AAD_LOGIN_SUCCESS');
export const loginFailed = createAction('AAD_LOGIN_FAILED');
export const logoutSuccess = createAction('AAD_LOGOUT_SUCCESS');
export const acquiredAccessTokenSuccess = createAction<AadToken>(
  'AAD_ACQUIRED_ACCESS_TOKEN_SUCCESS'
);
export const acquiredAccessTokenError = createAction<{
  errorCode: string;
  errorMessage: string;
  name: string;
}>('AAD_ACQUIRED_ACCESS_TOKEN_ERROR');

export const acquiredEmployeeDetailsSuccess = createAction<AadAccount>(
  'AAD_ACQUIRED_EMPLOYEE_DETAILS_SUCCESS'
);
export const setError = createAction<Array<NormalizedError> | null>(
  'AAD_SET_ERRORS'
);
export const setIsLoading = createAction('AAD_INITIALIZING');
export const setInitialized = createAction('AAD_INITIALIZED');

export const initialState: AadState = {
  isLoading: false,
  errors: null,
  data: {}
};

const aad = (builder: ActionReducerMapBuilder<AadState>) => {
  builder
    .addCase(loginSuccess, (state, action) => {
      state.isLoading = initialState.isLoading;
      state.data = action.payload;
      state.errors = initialState.errors;
    })
    .addCase(logoutSuccess, () => initialState)
    .addCase(acquiredAccessTokenSuccess, (state, action) => {
      state.data.token = action.payload;
    })
    .addCase(acquiredEmployeeDetailsSuccess, (state, action) => {
      state.isLoading = initialState.isLoading;
      state.data.account = state.data.account || ({} as AadAccount);
      state.data.account = { ...state.data.account, ...action.payload };
      state.errors = initialState.errors;
    })
    .addCase(setError, (state, action) => {
      state.isLoading = initialState.isLoading;
      state.errors = action.payload;
    })
    .addCase(loginFailed, (state) => {
      state.isLoading = initialState.isLoading;
      state.errors = [
        {
          code: 'LOGIN_FAILED',
          message: 'Login failed'
        }
      ];
      state.data = initialState.data;
    })
    .addCase(acquiredAccessTokenError, (state, action) => {
      const { errorCode, errorMessage, name } = action.payload;
      state.isLoading = initialState.isLoading;
      state.errors = [{ message: errorMessage, code: errorCode, name }];
    })
    .addCase(setIsLoading, (state) => {
      state.isLoading = true;
    })
    .addCase(setInitialized, (state) => {
      state.isLoading = initialState.isLoading;
    })
    .addDefaultCase((state) => state);
};

export const slice = createSlice({
  name: 'aad',
  initialState,
  reducers: {},
  extraReducers: aad
});

export const selectSlice = (state: RootState) => {
  return state.aad;
};
export const selectData = (state: RootState) => selectSlice(state).data;
export const selectAccessToken = (state: RootState) => {
  const data = selectData(state);
  if (!data.token) {
    return;
  }
  return data.token.accessToken;
};
export const selectAccount = (state: RootState) => selectData(state).account;
export const selectErrors = (state: RootState) => selectSlice(state).errors;
export const selectIsLoading = (state: RootState) =>
  selectSlice(state).isLoading;

export default slice.reducer;
